import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import { mutateData } from "@/core/api/api-methods";
import {
  invalidateAfterMutation,
  payrollBatchCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { payrollEndpoints } from "@/features/payroll/api/endpoints";
import type {
  PayrollBatchDto,
  PayrollBatchLineInputDto,
} from "@/features/payroll/api/dto";

interface Args {
  lineId: string;
  body: PayrollBatchLineInputDto;
}

/**
 * Corrige une ligne (nom, téléphone, montant, matricule) — `PUT
 * /PayrollBatches/{id}/lines/{lineId}`. La paie revient en `DRAFT`. Codes :
 * 200 ; 404 ; 422 (paie lancée).
 */
export const useUpdatePayrollLine = (batchId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["payroll", "payrollBatches", "updateLine", batchId],
    mutationFn: ({ lineId, body }: Args) =>
      mutateData({
        url: payrollEndpoints.payrollBatches.updateLine(batchId, lineId),
        reqBody: body as unknown as Record<string, unknown>,
        method: "PUT",
      }) as unknown as Promise<PayrollBatchDto>,
    onSuccess: () =>
      invalidateAfterMutation(queryClient, payrollBatchCacheKeys(batchId)),
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useUpdatePayrollLine failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "payroll" }
      );
    },
  });
};
